import React from "react";
import {
  TextField,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Button,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import BondAttorneyQuickAddDialog from "./BondAttorneyQuickAddDialog";
import BondQuickAddLawFirmDialog from "./BondQuickAddLawFirmDialog";
import { LawFirmFuzzyMatchDialogBound } from "./LawFirmFuzzyMatchDialog";
import { useAttorneyInformation } from "../hooks/useAttorneyInformation";
import {
  getAttorneyAutocompleteNoOptionsText,
  getAttorneySearchFieldHelperText,
} from "../utils/attorneySearch";

const AttorneyInformationBase = ({
  cfg,
  data,
  updateData,
  isGuest,
  globalData,
  errors = {},
}) => {
  const vm = useAttorneyInformation({
    cfg,
    data,
    updateData,
    isGuest,
    globalData,
  });

  const {
    setAttorneyInvolved,
    lawFirms,
    lawFirmLocations,
    attorneys,
    loadingLawFirms,
    loadingLocations,
    loadingAttorneys,
    openAttorneyDialog,
    openLawFirmDialog,
    openAddressDialog,
    newAttorney,
    setNewAttorney,
    newLawFirm,
    setNewLawFirm,
    newAddress,
    setNewAddress,
    fuzzyMatchDialogOpen,
    setFuzzyMatchDialogOpen,
    fuzzyMatchResult,
    setFuzzyMatchResult,
    fuzzyMatchLocationDialogOpen,
    setFuzzyMatchLocationDialogOpen,
    fuzzyMatchLocationResult,
    setFuzzyMatchLocationResult,
    handleLawFirmChange,
    handleOpenAttorneyDialog,
    handleCloseAttorneyDialog,
    handleOpenLawFirmDialog,
    handleCloseLawFirmDialog,
    handleOpenAddressDialog,
    handleCloseAddressDialog,
    handleAddAttorney,
    handleAddLawFirm,
    handleUseExistingLawFirm,
    handleCreateNewLawFirmAnyway,
    handleAddAddress,
    handleUseExistingLocation,
    handleCreateNewLocationAnyway,
    showGuestForm,
    showRegisteredForm,
    showAttorneyToggle,
    radioGroupValue,
    attorneySearchInput,
    handleAttorneySearchInputChange,
  } = vm;

  const showErrors = cfg.showAutocompleteErrors;
  const useDebouncedSearch = !!cfg.useDebouncedAttorneySearch;
  const attorneyFieldError = showErrors ? errors.attorneyOfRecord : undefined;
  const attorneySearchHelperText = useDebouncedSearch
    ? attorneyFieldError ||
      getAttorneySearchFieldHelperText(
        attorneySearchInput,
        loadingAttorneys,
        attorneys.length
      )
    : attorneyFieldError;

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Attorney Information
      </Typography>

      {showAttorneyToggle() && (
        <FormControl component="fieldset" sx={{ mb: 4 }}>
          <Typography variant="body1">
            Will an attorney remain involved throughout the duration of the case?
          </Typography>
          <RadioGroup
            row
            name="attorneyInvolved"
            value={radioGroupValue}
            onChange={(e) => setAttorneyInvolved(e.target.value)}
          >
            <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="No" control={<Radio />} label="No" />
          </RadioGroup>
        </FormControl>
      )}

      {showGuestForm() && (
        <Box className="grid grid-cols-1 gap-4">
          <TextField
            label="Law Firm Name"
            name="guest_lawfirm_name"
            value={data.guest_lawfirm_name || ""}
            onChange={(e) =>
              updateData({ ...data, guest_lawfirm_name: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Law Firm Address"
            name="guest_lawfirmaddress_name"
            value={data.guest_lawfirmaddress_name || ""}
            onChange={(e) =>
              updateData({ ...data, guest_lawfirmaddress_name: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Attorney Name"
            name="guest_attorney_name"
            value={data.guest_attorney_name || ""}
            onChange={(e) =>
              updateData({ ...data, guest_attorney_name: e.target.value })
            }
            fullWidth
          />
        </Box>
      )}

      {showRegisteredForm() && (
        <Box className="grid grid-cols-1 gap-4">
          <Autocomplete
            options={lawFirms}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLawFirms}
            value={lawFirms.find((firm) => firm.value === data.lawFirm) || null}
            onChange={handleLawFirmChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Law Firm"
                variant="outlined"
                fullWidth
                error={showErrors && !!errors.lawFirm}
                helperText={showErrors ? errors.lawFirm : undefined}
                slotProps={{ input: { ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLawFirms ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenLawFirmDialog}
          >
            Law firm not in the list?
          </Typography>

          <Autocomplete
            options={lawFirmLocations}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingLocations}
            value={
              lawFirmLocations.find((loc) => loc.value === data.lawFirmAddress) ||
              null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                lawFirmAddress: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Law Firm Address"
                variant="outlined"
                fullWidth
                error={showErrors && !!errors.lawFirmAddress}
                helperText={showErrors ? errors.lawFirmAddress : undefined}
                slotProps={{ input: { ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingLocations ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenAddressDialog}
          >
            Address not in the list?
          </Typography>

          <Autocomplete
            options={attorneys}
            getOptionLabel={(option) => option.label || ""}
            loading={loadingAttorneys}
            filterOptions={useDebouncedSearch ? (options) => options : undefined}
            onInputChange={useDebouncedSearch ? handleAttorneySearchInputChange : undefined}
            noOptionsText={
              useDebouncedSearch
                ? getAttorneyAutocompleteNoOptionsText(
                    attorneySearchInput,
                    loadingAttorneys
                  )
                : undefined
            }
            value={
              attorneys.find((attorney) => attorney.value === data.attorneyOfRecord) ||
              null
            }
            onChange={(event, newValue) => {
              updateData({
                ...data,
                attorneyOfRecord: newValue ? newValue.value : "",
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Attorney of Record"
                variant="outlined"
                fullWidth
                error={showErrors && !!errors.attorneyOfRecord}
                helperText={attorneySearchHelperText}
                slotProps={{ input: { ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingAttorneys ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}}
              />
            )}
          />
          <Typography
            variant="body2"
            className="text-blue-500 cursor-pointer -mt-2"
            sx={{ textAlign: "right" }}
            onClick={handleOpenAttorneyDialog}
          >
            Attorney not in the list?
          </Typography>
        </Box>
      )}

      <BondAttorneyQuickAddDialog
        open={openAttorneyDialog}
        onClose={handleCloseAttorneyDialog}
        onSubmit={handleAddAttorney}
        attorney={newAttorney}
        setAttorney={setNewAttorney}
      />

      <BondQuickAddLawFirmDialog
        open={openLawFirmDialog}
        onClose={handleCloseLawFirmDialog}
        onSubmit={handleAddLawFirm}
        lawFirm={newLawFirm}
        setLawFirm={setNewLawFirm}
      />

      <LawFirmFuzzyMatchDialogBound
        open={fuzzyMatchDialogOpen}
        setOpen={setFuzzyMatchDialogOpen}
        fuzzyMatchResult={fuzzyMatchResult}
        setFuzzyMatchResult={setFuzzyMatchResult}
        onUseExisting={handleUseExistingLawFirm}
        onCreateAnyway={handleCreateNewLawFirmAnyway}
      />

      <Dialog
        open={fuzzyMatchLocationDialogOpen}
        onClose={() => {
          setFuzzyMatchLocationDialogOpen(false);
          setFuzzyMatchLocationResult(null);
        }}
      >
        <DialogTitle>Similar location found</DialogTitle>
        <DialogContent>
          <Typography>
            A similar location was found for this address
            {fuzzyMatchLocationResult?.score
              ? ` (match score: ${Number(fuzzyMatchLocationResult.score).toFixed(0)}%)`
              : ""}
            . Do you want to use this existing location or add a new one?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUseExistingLocation} color="primary">
            Use existing
          </Button>
          <Button onClick={handleCreateNewLocationAnyway} color="secondary">
            Add new anyway
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAddressDialog} onClose={handleCloseAddressDialog} fullWidth>
        <DialogTitle>Add New Address</DialogTitle>
        <DialogContent>
          {["address", "zip", "city", "county", "state", "phone"].map((field) => (
            <TextField
              key={field}
              label={field === "zip" ? "ZIP" : field.charAt(0).toUpperCase() + field.slice(1)}
              name={field}
              value={newAddress[field]}
              onChange={(e) => setNewAddress({ ...newAddress, [field]: e.target.value })}
              fullWidth
              required
              margin="normal"
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddressDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddAddress} color="primary">
            Add Address
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttorneyInformationBase;
